
import React from 'react';
import { Star, Play, CheckCircle, BookOpen } from 'lucide-react';
import { Question } from '../data/types';
import { getTags } from '../utils';

interface FavoritesReviewProps {
    favoriteQuestions: number[];
    questions: Question[];
    toggleFavorite: (id: number) => void;
    startQuizWithFavorites: () => void;
}

export const FavoritesReviewView = ({ favoriteQuestions, questions, toggleFavorite, startQuizWithFavorites }: FavoritesReviewProps) => {
    // Filter the full question list to find those in the favorites list
    const favList = questions.filter(q => favoriteQuestions.includes(q.id));

    if (favList.length === 0) {
        return (
            <div className="flex-1 bg-gray-50 flex flex-col items-center justify-center p-8 text-center animate-fade-in">
                <div className="w-24 h-24 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                    <Star className="w-12 h-12 text-orange-500 fill-current" />
                </div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">目前沒有收藏的題目</h2>
                <p className="text-gray-500 max-w-md">
                    在測驗或瀏覽卡片時，點擊星星圖示即可將經典或重要題目加入此處，方便日後複習。
                </p>
            </div>
        );
    }

    return (
        <div className="flex-1 bg-gray-50 overflow-hidden flex flex-col">
            {/* Header / Stats */}
            <div className="bg-white border-b border-gray-200 px-6 py-6 shadow-sm shrink-0">
                <div className="max-w-5xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
                    <div>
                        <div className="flex items-center text-orange-600 mb-1">
                            <Star className="w-6 h-6 mr-2 fill-current" />
                            <h2 className="text-2xl font-bold font-serif">我的最愛</h2>
                        </div>
                        <p className="text-gray-500 text-sm">
                            已收藏：<span className="font-bold text-orange-600 text-lg">{favList.length}</span> 題
                            <span className="mx-2">|</span>
                            經典題型、重點觀念，考前快速複習。
                        </p>
                    </div>
                    <button
                        onClick={startQuizWithFavorites}
                        className="flex items-center px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-bold shadow-lg transition-transform transform hover:scale-105"
                    >
                        <Play className="w-5 h-5 mr-2" />
                        測驗收藏題 ({favList.length})
                    </button>
                </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-4 md:p-8">
                <div className="max-w-5xl mx-auto space-y-4">
                    {favList.map((q) => (
                        <div key={q.id} className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow group flex flex-col md:flex-row gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-xs font-mono text-gray-400">ID: {q.id}</span>
                                    {getTags(q.category).map(tag => (
                                        <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded border border-gray-200">
                                            {tag}
                                        </span>
                                    ))}
                                </div>
                                <h3 className="text-lg font-bold text-gray-800 mb-3 line-clamp-2 md:line-clamp-none">
                                    {q.text}
                                </h3>
                                
                                <div className="hidden group-hover:block bg-orange-50 p-3 rounded-lg border border-orange-100 mt-2 animate-fade-in">
                                    <div className="flex items-start text-sm text-gray-700">
                                        <BookOpen className="w-4 h-4 text-orange-500 mr-2 mt-0.5 shrink-0" />
                                        <div>
                                            <span className="font-bold text-orange-700 mb-1 block">正確解答：</span>
                                            {q.explanation}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex md:flex-col justify-end items-end gap-2 border-t md:border-t-0 border-gray-100 pt-3 md:pt-0">
                                <button
                                    onClick={() => toggleFavorite(q.id)}
                                    className="flex items-center px-3 py-1.5 text-sm text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors border border-transparent hover:border-orange-200"
                                    title="取消收藏"
                                >
                                    <Star className="w-4 h-4 mr-1 fill-current" />
                                    取消收藏
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
